<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  label: string
  value: string
  subline?: string
  deltaPct?: number | null
  delta?: number | null
  accent?: 'emerald' | 'white' | 'muted'
  hero?: boolean
}>()

const { t } = useI18n()

function formatDelta(v: number): string {
  return (v >= 0 ? '+' : '') + v.toFixed(1) + '%'
}

function formatDeltaCount(v: number): string {
  return (v >= 0 ? '+' : '') + v.toString()
}
</script>

<template>
  <div
    class="bg-slate-800 border rounded-2xl p-6 flex flex-col gap-2"
    :class="hero ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-slate-700'"
  >
    <p class="text-slate-400 text-sm font-medium">{{ label }}</p>
    <p
      class="text-3xl font-bold leading-tight"
      :class="{
        'text-emerald-400': accent === 'emerald',
        'text-white': accent === 'white' || !accent,
        'text-slate-400': accent === 'muted',
      }"
    >
      {{ value }}
    </p>

    <!-- Delta badge: prefer deltaPct, fall back to count delta -->
    <div class="flex items-center gap-2 min-h-[1.25rem]">
      <!-- Percentage delta -->
      <template v-if="deltaPct !== undefined && deltaPct !== null">
        <span
          class="inline-flex items-center gap-1 text-xs font-semibold"
          :class="deltaPct >= 0 ? 'text-emerald-400' : 'text-red-400'"
        >
          <span>{{ deltaPct >= 0 ? '▲' : '▼' }}</span>
          <span>{{ formatDelta(Math.abs(deltaPct * 100)) }}</span>
        </span>
      </template>
      <!-- Count delta (no percentage) -->
      <template v-else-if="delta !== undefined && delta !== null">
        <span
          class="inline-flex items-center gap-1 text-xs font-semibold"
          :class="delta >= 0 ? 'text-emerald-400' : 'text-red-400'"
        >
          <span>{{ delta >= 0 ? '▲' : '▼' }}</span>
          <span>{{ formatDeltaCount(delta) }}</span>
        </span>
      </template>
      <!-- Null delta = no data for previous period -->
      <template v-else-if="deltaPct === null || delta === null">
        <span class="text-slate-500 text-xs">{{ t('admin.cockpit.kpi.delta_none') }}</span>
      </template>
    </div>

    <p v-if="subline" class="text-slate-500 text-xs leading-snug">{{ subline }}</p>
  </div>
</template>
